import Color from 'color';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import styled, { createGlobalStyle, withTheme } from 'styled-components';
import { Manager, Popper, Reference } from 'react-popper';
import { defaultTheme } from '../../../constants/appConfig';
import { OnClickOutside } from '../../common/click-outside';
import DomHolder from '../../common/DomHolder';
import Portal from '../../common/Portal';
import { AutoScroller, Scrollbar } from '../../common/scrollbar';
import useCombinedRefs from '../../hooks/useCombinedRefs';
import Input from '../Input';
import AutoCompleteLoader from './AutoCompleteLoader';
import ClearIcon from '../../../assets/svg/trash.svg';

const GlobalContainerStyle = createGlobalStyle`
  #__tooltip-dd-modal-container {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
  }
`;

const UiAutocompleteDropdown = styled.div`
  .ui-autocomplete-item {
    color: ${props => (props.theme.formTheme === 'dark' ? '#fff' : '#000')};

    &.focused {
      background-color: ${props => (
    /* eslint-disable indent */
        Color(props.theme ? props.theme.primaryBgColor : defaultTheme.primaryBgColor)
          .fade(props.theme.formTheme === 'dark' ? 0.7 : 0.96)
          .string()
      )};
    /* eslint-enable indent */
    }
    &:hover {
      /*color: ${props => (props.theme ? props.theme.primaryBgColor : defaultTheme.primaryBgColor)};*/
    }
  }
`;

const UiAutocompleteInput = styled.div`
  &.active {
    .ui-input-component {
      input {
        border-color: ${props => (props.theme ? props.theme.primaryBgColor : defaultTheme.primaryBgColor)};
      }
    }
  }

`;

const keyNameToCodeAndKeyCode = {
  tab: { keyCode: 9, code: 'Tab' },
  arrowUp: { keyCode: 38, code: 'ArrowUp' },
  arrowDown: { keyCode: 40, code: 'ArrowDown' },
  esc: { keyCode: 27, code: 'Escape' },
  enter: { keyCode: 13, code: 'Enter' },
};

const inputPropNames = ['size', 'state', 'prefix', 'suffix', 'id', 'name', 'placeholder', 'disabled', 'rows'];

const SafeElement = typeof Element === 'undefined' ? () => {} : Element;

class AutoComplete extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    dataset: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }).isRequired,
    ).isRequired,
    size: PropTypes.oneOf(['s', 'm', 'l']),
    state: PropTypes.oneOf(['valid', 'invalid']),
    prefix: PropTypes.node,
    suffix: PropTypes.node,
    id: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    minSearchLength: PropTypes.number,
    theme: PropTypes.object,
    renderItem: PropTypes.func,
    initialValue: PropTypes.string,
    inputRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.instanceOf(SafeElement) }),
    ]),
    inputComponent: PropTypes.elementType,
    onSearch: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    onInputChange: PropTypes.func,
    onReset: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    popperModifiers: PropTypes.array,
  };

  static defaultProps = {
    size: 'm',
    minSearchLength: 3,
    initialValue: '',
    popperModifiers: [
      { name: 'preventOverflow', enabled: false },
      { name: 'flip', enabled: false },
      { name: 'offset', options: { offset: [1, 0] } },
    ],
    theme: {},
    onInputChange: () => {},
    inputComponent: Input,
  };

  mainRef = React.createRef();
  dropdown = React.createRef();
  inputRef = React.createRef();
  // clickPromise = Promise.resolve();
  // eslint-disable-next-line react/sort-comp
  // clickPromiseResolve = () => {};

  state = {
    value: this.props.initialValue ?? '',
    activeItemId: '',
    opened: false,
    loading: false,
  };

  lastSearchedValue = '';

  componentDidUpdate(prevProps) {
    const { dataset } = this.props;

    if (dataset !== prevProps.dataset) {
      this.setState({ activeItemId: dataset.length > 0 ? dataset[0].id : '' });
    }
  }

  highlight = (text) => {
    const { value } = this.state;
    const escaped = value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regexp = new RegExp(escaped.split(' ').join('|'), 'ig');
    const html = text.replace(regexp, '<span class="highlighted">$&</span>'); // TODO: potential vulnerability

    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  handleClickOutside = (e) => {
    if (!this.mainRef.current.contains(e.target)) {
      this.setState({ opened: false });
    }
  }

  handleChange = (value) => {
    const { minSearchLength, onSearch, onReset, onInputChange } = this.props;
    // const { value } = e.target;

    this.setState({ value, opened: true, activeItemId: '' });

    if (value.trim().length >= minSearchLength) {
      this.lastSearchedValue = value;
      this.setState({ loading: true });
      (onSearch(value) || Promise.resolve())
        .then(this.finishLoading, this.finishLoading);
    }

    onInputChange(value);

    onReset?.(value);
    // onBlur?.(e);
  };

  handleFocus = (e) => {
    const { minSearchLength, onSearch, onFocus } = this.props;
    const { value } = this.state;

    this.setState({ opened: true });

    if (value.trim().length >= minSearchLength && this.lastSearchedValue !== value) {
      this.lastSearchedValue = value;
      this.setState({ loading: true });
      (onSearch(value) || Promise.resolve())
        .then(this.finishLoading, this.finishLoading);
    }

    onFocus?.(e);
  };

  handleBlur = (e) => {
    const { onBlur } = this.props;
    e?.persist(); // react synthetic events...

    setTimeout(() => {
      onBlur?.(e);
    });
    // this.clickPromise.then(() => {
    // });
    // this.setState({ opened: false });
  };

  handleKeyDown = (e) => {
    const { dataset, minSearchLength } = this.props;
    const { value, activeItemId } = this.state;

    if (value.trim().length < minSearchLength) {
      return;
    }

    const key = e.code !== undefined ? 'code' : 'keyCode';

    const { arrowUp, arrowDown, esc, enter } = keyNameToCodeAndKeyCode;

    if (e[key] === keyNameToCodeAndKeyCode.tab[key]) {
      this.setState({ opened: false });
      return;
    }

    // arrowup || arrowdown || esc || enter
    if ([arrowUp[key], arrowDown[key], esc[key], enter[key]].includes(e[key])) {
      const activeIndex = activeItemId ? dataset.findIndex(item => item.id === activeItemId) : -1;

      switch (e[key]) {
        case esc[key]:
          e.preventDefault();
          this.setState({ value: '', activeItemId: '', opened: false });
          break;

        case arrowUp[key]:
          e.preventDefault();

          if (activeIndex > 0) {
            this.setState({ activeItemId: dataset[activeIndex - 1].id, opened: true });
          } else {
            this.setState({ opened: true });
          }
          break;

        case arrowDown[key]:
          e.preventDefault();

          if (activeIndex < dataset.length - 1) {
            this.setState({ activeItemId: dataset[activeIndex + 1].id, opened: true });
          } else {
            this.setState({ opened: true });
          }
          break;

        case enter[key]:
          e.preventDefault();

          const item = dataset[activeIndex];

          if (item) {
            this.handleItemSelect(item);
          }
          break;

        default:
          break;
      }
    }
  };

  handleDropdownMouseDown = e => e.preventDefault();

  handleClearClick = (e) => {
    e.preventDefault();
    this.handleChange('');
  };

  handleItemClick = (e) => {
    const { dataset } = this.props;
    const { id } = e.currentTarget.dataset;
    const item = dataset.find(item => item.id === id);

    this.handleItemSelect(item);
    // this.clickPromiseResolve();
  };

  handleItemSelect = (item) => {
    if (item.disabled) return;

    const { onSelect } = this.props;

    onSelect(item);
    this.setState({ value: item.label, opened: false }, () => {
      this.inputRef.current.input.blur();
    });
  };

  handleItemMouseEnter = e => this.setState({ activeItemId: e.target.dataset.id });

  handleItemMouseLeave = () => {
    this.setState({ activeItemId: '' });
    // this.clickPromiseResolve();
  }

  // handleItemMouseDown = () => {
  //   this.clickPromise = new Promise((resolve) => {
  //     this.clickPromiseResolve = resolve;
  //   });
  // };

  finishLoading = () => this.setState({ loading: false });

  renderItem = ({ label }, highlight) => highlight(label);

  renderItems = () => {
    const {
      dataset,
      minSearchLength,
      renderItem = this.renderItem,
    } = this.props;
    const { value, activeItemId, loading } = this.state;

    if (value.trim().length < minSearchLength) {
      return null;
    }

    if (dataset.length === 0) {
      return (
        <div className="ui-autocomplete-items-empty">
          <div className="ui-autocomplete-items-empty-label">{loading ? 'Загрузка...' : 'Ничего не найдено'}</div>
        </div>
      );
    }

    return dataset.map(item => (
      <div
        key={item.id}
        data-id={item.id}
        className={classnames('ui-autocomplete-item', { focused: activeItemId === item.id, disabled: item.disabled })}
        onClick={this.handleItemClick}
        // onMouseDown={this.handleItemMouseDown}
        onMouseEnter={this.handleItemMouseEnter}
        onMouseLeave={this.handleItemMouseLeave}
      >
        {renderItem(item, this.highlight)}
      </div>
    ));
  };

  renderRef = ({ ref }) => {
    const {
      className, disabled, size, state, minSearchLength, theme,
    } = this.props;
    const { opened, inputFocused, value } = this.state;
    const active = opened && value.trim().length >= minSearchLength;

    return (
      <CSSTransition classNames="ui-autocomplete-input" in={active} timeout={150}>
        <UiAutocompleteInput
          className={classnames(`ui-autocomplete-input ${theme.inputStyle}`, className, `size-${size}`, `state-${state}`, { focused: inputFocused, active, disabled, dark: theme.formTheme === 'dark' })}
        >
          {this.renderInput(ref)}
          <CSSTransition
            classNames="clear-btn"
            timeout={150}
            in={value.trim().length > 1}
            mountOnEnter
            unmountOnExit
          >
            <button type="button" className="clear-btn" onMouseDown={this.handleClearClick}>
              <ClearIcon />
            </button>
          </CSSTransition>
        </UiAutocompleteInput>
      </CSSTransition>
    );
  };

  renderInput(ref) {
    const { value } = this.state;
    const { inputRef, inputComponent: InputComponent, label, valid, message, iconPath } = this.props;

    const inputProps = inputPropNames.reduce((props, name) => {
      props[name] = this.props[name];
      return props;
    }, {});

    return (
      <InputComponent
        {...inputProps}
        ref={this.inputRef}
        inputRef={useCombinedRefs(ref, inputRef, this.mainRef)}
        className="ui-input-component"
        value={value}
        label={label}
        valid={valid}
        message={message}
        iconPath={iconPath}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
      />
    );
  }

  renderPopper = clickOutsideRef => ({ ref, style, update }) => {
    const { dataset, minSearchLength, size, theme } = this.props;
    const { value, opened, loading } = this.state;
    const active = opened && value.trim().length >= minSearchLength;
    // const width = '600px';
    const width = `${this.mainRef.current?.offsetWidth}px`;
    this.updateDropdownPosition = update;

    return (
      <CSSTransition
        classNames="ui-autocomplete-dropdown"
        timeout={150}
        in={active}
        mountOnEnter
        unmountOnExit
      >
        <UiAutocompleteDropdown
          className={classnames('ui-autocomplete-dropdown', `size-${size}`, { opened, dark: theme.formTheme === 'dark' })}
          ref={useCombinedRefs(ref, this.dropdown)}
          style={{ ...style, width, zIndex: 100 }}
          onMouseDown={this.handleDropdownMouseDown}
        >
          <AutoCompleteLoader loading={loading && dataset.length > 0} />
          <div className="ui-autocomplete-scroll-wrap" ref={clickOutsideRef}>
            <AutoScroller selector=".ui-autocomplete-item.focused">
              {sbRef => (
                <Scrollbar className="ui-autocomplete-scroll" ref={sbRef}>
                  <div className="ui-autocomplete-items">
                    <DomHolder>
                      {active && this.renderItems()}
                    </DomHolder>
                  </div>
                </Scrollbar>
              )}
            </AutoScroller>
          </div>
        </UiAutocompleteDropdown>
      </CSSTransition>
    );
  };

  render() {
    const { minSearchLength, popperModifiers } = this.props;
    const { value, opened } = this.state;
    const active = opened && value.trim().length >= minSearchLength;

    return (
      <Manager>
        <Reference>
          {this.renderRef}
        </Reference>
        <OnClickOutside onClickOutside={this.handleClickOutside} active={active}>
          {ref => (
            <Portal id="__tooltip-dd-modal-container">
              <GlobalContainerStyle />
              <Popper placement="bottom" modifiers={popperModifiers}>
                {this.renderPopper(ref)}
              </Popper>
            </Portal>
          )}
        </OnClickOutside>
      </Manager>
    );
  }
}

// export default withFormControl(AutoComplete);
export default withTheme(AutoComplete);
